import rehypeStringify from 'rehype-stringify';
import remarkBreaks from 'remark-breaks';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import type { CSSProperties, ReactNode } from 'react';
import classNames from './classNames';
import {
    rehypeMarkdownStatuses,
    rehypeSafeMarkdownLinks,
    remarkPreserveVisibleBlankLines,
} from './markdownTransforms';

const MARKDOWN_PREVIEW_STYLES = [
    '[&_a]:text-primary [&_a]:underline',
    '[&_b]:font-semibold',
    '[&_blockquote]:border-l-4 [&_blockquote]:border-neutral-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-neutral-700',
    '[&_code]:rounded [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.9em]',
    '[&_em]:italic',
    '[&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:leading-tight',
    '[&_h2]:text-xl [&_h2]:font-semibold [&_h2]:leading-tight',
    '[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:leading-snug',
    '[&_h4]:text-base [&_h4]:font-semibold [&_h4]:leading-snug',
    '[&_h5]:text-sm [&_h5]:font-semibold [&_h5]:leading-snug',
    '[&_h6]:text-sm [&_h6]:font-medium [&_h6]:leading-snug',
    '[&_i]:italic',
    '[&_ol]:list-decimal [&_ol]:pl-6',
    '[&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-slate-900 [&_pre]:p-3 [&_pre]:font-mono [&_pre]:text-sm [&_pre]:text-slate-100',
    '[&_strong]:font-semibold',
    '[&_ul]:list-disc [&_ul]:pl-6',
].join(' ');

export function markdown(input: string) {
    return String(
        unified()
            .use(remarkParse)
            // Turns newlines into <br>'s:
            .use(remarkBreaks)
            // Preserves blank-line runs as explicit line breaks between block nodes.
            .use(remarkPreserveVisibleBlankLines)
            // Turns markdown into AST:
            .use(remarkRehype)
            // Renders application-generated inline statuses as badges:
            .use(rehypeMarkdownStatuses)
            // Allows external links while stripping unsafe URL protocols.
            .use(rehypeSafeMarkdownLinks)
            // Turns AST into s string:
            .use(rehypeStringify)
            .processSync(input)
    );
}

interface MarkdownProps {
    children: string;
}

interface MarkdownPreviewProps {
    markdownText: string;
    className?: string;
    style?: CSSProperties;
    testId?: string;
}

export function Markdown({ children }: MarkdownProps) {
    return <span dangerouslySetInnerHTML={{ __html: markdown(children) }}></span>;
}

export function MarkdownPreview({
    markdownText,
    className,
    style,
    testId,
}: MarkdownPreviewProps): JSX.Element {
    return (
        <div
            className={classNames('text-base text-slate-800', MARKDOWN_PREVIEW_STYLES, className)}
            data-testid={testId}
            style={style}
            dangerouslySetInnerHTML={{ __html: markdown(markdownText) }}
        />
    );
}
